import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LayoutGrid, Target, List, Archive, Search, SlidersHorizontal, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Surface } from '../layout/Surface';
import { cn } from '../../lib/utils';
import { LEVELS, PRIORITY_META, IMPORTANCE_META, DIFFICULTY_META } from './taskMeta';
import { DUE_BUCKETS, dueBucket } from './taskDates';
import BulkActions from './BulkActions';

/**
 * TaskControlBar — the instrument strip above the board.
 *
 * Replaces the old "Mission Control · 8 do · 0 prog · 45 ok" page header, which
 * cost ~96px to restate counts the column badges already carry, and made Tasks
 * the only route in the app with a title. This is roughly half the height and
 * actually does something.
 *
 * Deliberately slim. Filter detail lives in a popover and only *active* filters
 * spill back out as chips — a filter you can't see is a filter that makes tasks
 * vanish for no visible reason, so the chips are not optional.
 */

export const VIEWS = [
    { id: 'kanban', icon: LayoutGrid, label: 'Board' },
    { id: 'matrix', icon: Target, label: 'Matrix' },
    { id: 'list', icon: List, label: 'List' },
    // Not a fourth lens on the same tasks — a different pool. It rides in the
    // same pill because that is where you already look to change what the page
    // is showing, and a second switcher would be two answers to one question.
    // No count badge: a permanent "23" is exactly the nagging the backlog exists
    // to remove.
    // A box you put things in and can open again — not the down-arrow-to-line
    // this started as, which is the universal download glyph and read as
    // "export my tasks" sitting in a row of view switchers.
    { id: 'backlog', icon: Archive, label: 'Backlog' },
];

// Generated from the shared task vocabulary, so a filter chip and the badge on
// the card it matches are literally the same colours — no second palette to
// keep in sync. Ordered as they appear in a card's badge row.
const FILTER_FIELDS = [
    { key: 'priority', label: 'Priority', prefix: 'P', meta: PRIORITY_META },
    { key: 'importance', label: 'Importance', prefix: 'I', meta: IMPORTANCE_META },
    { key: 'difficulty', label: 'Difficulty', prefix: 'D', meta: DIFFICULTY_META },
];

// High reads first; LEVELS is stored low → high.
const LEVELS_DESC = [...LEVELS].reverse();

const DUE_CHIP = {
    overdue: 'bg-red-500/20 text-red-400 border-red-500/40',
    today: 'bg-primary/20 text-primary border-primary/40',
    week: 'bg-accent/15 text-accent border-accent/30',
    none: 'bg-white/5 text-muted-foreground border-white/15',
};

export const EMPTY_FILTERS = { priority: [], importance: [], difficulty: [], due: [] };

/**
 * Pure: the same predicate feeds the board, the matrix and the list, so a task
 * hidden in one view is hidden in all of them.
 */
export function filterTasks(tasks, query, filters) {
    const q = query.trim().toLowerCase();
    const active = FILTER_FIELDS.filter((f) => filters[f.key]?.length);
    const due = filters.due ?? [];
    if (!q && active.length === 0 && due.length === 0) return tasks;

    // One clock for the whole pass, so a task can't land in a different bucket
    // than the one before it because midnight ticked mid-filter.
    const now = new Date();

    return tasks.filter((task) => {
        if (q) {
            const haystack = `${task.title ?? ''} ${task.description ?? ''}`.toLowerCase();
            if (!haystack.includes(q)) return false;
        }
        if (due.length && !due.includes(dueBucket(task.due_date, now))) return false;
        // `|| 'medium'` mirrors LevelBadge, which renders an unset field as
        // Medium. Without it, filtering by Medium would silently drop every
        // task created before that field existed.
        return active.every((f) => filters[f.key].includes(task[f.key] || 'medium'));
    });
}

export default function TaskControlBar({
    activeView,
    onViewChange,
    query,
    onQueryChange,
    filters,
    onFiltersChange,
    shown,
    total,
    readOnly,
    onBulkStatus,
    onBulkDelete,
    onBulkDemote,
    onBulkPromote,
}) {
    const searchRef = useRef(null);

    // Share-link viewers get three segments, not four: the backlog is private
    // thinking, and the endpoint already refuses to send it.
    const views = useMemo(
        () => (readOnly ? VIEWS.filter((v) => v.id !== 'backlog') : VIEWS),
        [readOnly]
    );
    const activeIndex = views.findIndex((v) => v.id === activeView);

    // ── The sliding indicator ────────────────────────────────────────────────
    // Measured off the button it is highlighting, not computed as 1/n of the
    // track. The segments are content-sized — "List" is nowhere near as wide as
    // "Backlog", the labels vanish entirely below `sm`, and there is a 4px gap
    // between each pair that a percentage split cannot see. Equal quarters were
    // wrong for every tab; they were just most obvious on the last one, where
    // the error accumulates and pushed the highlight past the container's
    // rounded edge.
    const trackRef = useRef(null);
    const tabRefs = useRef([]);
    const [indicator, setIndicator] = useState(null);

    useLayoutEffect(() => {
        // Drop the fourth ref when a share link takes the tab count to three,
        // or the observer below attaches to a detached node forever.
        const tabs = tabRefs.current;
        tabs.length = views.length;

        const measure = () => {
            const el = tabs[activeIndex];
            // offsetLeft and `left` are both relative to the padding box of the
            // positioned ancestor, which is the track — so this needs no
            // correction for the p-1.
            if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
        };

        // Before paint, so the first frame is already in the right place and
        // the transition has nothing to animate in from.
        measure();

        // Widths move without the active tab changing: the `sm` breakpoint
        // shows and hides every label, and a late-loading font re-measures all
        // of them. Watching the track alone would miss a tab that reflows
        // without changing the track's own size.
        const ro = new ResizeObserver(measure);
        if (trackRef.current) ro.observe(trackRef.current);
        for (const tab of tabs) if (tab) ro.observe(tab);
        return () => ro.disconnect();
    }, [activeIndex, views]);

    const toggle = (key, value) => {
        const current = filters[key] ?? [];
        onFiltersChange({
            ...filters,
            [key]: current.includes(value)
                ? current.filter((v) => v !== value)
                : [...current, value],
        });
    };

    const activeChips = useMemo(() => {
        const chips = FILTER_FIELDS.flatMap((field) =>
            (filters[field.key] ?? []).map((value) => ({
                group: field.key,
                value,
                label: `${field.prefix}:${field.meta[value].label}`,
                chip: field.meta[value].chip,
            }))
        );
        for (const value of filters.due ?? []) {
            const bucket = DUE_BUCKETS.find((b) => b.id === value);
            if (bucket) {
                chips.push({ group: 'due', value, label: bucket.label, chip: DUE_CHIP[value] });
            }
        }
        return chips;
    }, [filters]);

    // `/` focuses search, the way every list-heavy tool does it. Guarded on the
    // active element so it stays a slash when you're typing a task title.
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
            const el = document.activeElement;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
            e.preventDefault();
            searchRef.current?.focus();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, []);

    // Show the second row whenever something is narrowing the board — including
    // a search with no filter chips, where the count is the only feedback that
    // the board isn't showing everything.
    const isNarrowed = activeChips.length > 0 || query.trim().length > 0;

    const dueActive = filters.due ?? [];

    const dueToggles = (
        <>
            {DUE_BUCKETS.map((bucket) => {
                const on = dueActive.includes(bucket.id);
                return (
                    <button
                        key={bucket.id}
                        onClick={() => toggle('due', bucket.id)}
                        aria-pressed={on}
                        className={cn(
                            'rounded-lg border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-all',
                            on
                                ? DUE_CHIP[bucket.id]
                                : 'border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground'
                        )}
                    >
                        {bucket.label}
                    </button>
                );
            })}
        </>
    );

    return (
        <Surface as="header" variant="bar" aria-label="Task controls" className="flex-shrink-0">
            <div className="flex items-center gap-2 px-2 py-2 md:gap-3 md:px-3">
                {/* View switcher — sliding pill */}
                <div ref={trackRef} className="relative flex flex-shrink-0 items-center gap-1 rounded-2xl bg-secondary/30 p-1">
                    <div
                        aria-hidden
                        className={cn(
                            'absolute top-1 bottom-1 rounded-xl bg-primary shadow-lg transition-all duration-300 ease-out',
                            // Nothing to show until the first measurement lands.
                            // It lands before paint, so this is only ever true
                            // on a server render or a detached tree.
                            !indicator && 'opacity-0'
                        )}
                        style={{ left: indicator?.left ?? 0, width: indicator?.width ?? 0 }}
                    />
                    {views.map((view, i) => {
                        const ViewIcon = view.icon;
                        const isActive = activeView === view.id;
                        return (
                            <button
                                key={view.id}
                                ref={(el) => { tabRefs.current[i] = el; }}
                                onClick={() => onViewChange(view.id)}
                                aria-pressed={isActive}
                                className={cn(
                                    'relative z-10 flex items-center justify-center gap-2 rounded-xl px-3 py-1.5 text-sm font-medium transition-colors duration-200 sm:px-4',
                                    isActive
                                        ? 'text-primary-foreground'
                                        : 'text-muted-foreground hover:text-foreground'
                                )}
                            >
                                <ViewIcon
                                    className={cn(
                                        'h-4 w-4 transition-transform duration-200',
                                        isActive && 'scale-110'
                                    )}
                                />
                                <span className="hidden sm:inline">{view.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Search. Capped rather than left to fill: on a wide monitor a
                    flex-1 input stretches to ~1000px and the bar reads as one
                    giant text field with two controls stuck to the ends. */}
                <div className="relative min-w-0 flex-1 sm:max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <input
                        ref={searchRef}
                        type="text"
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                onQueryChange('');
                                e.currentTarget.blur();
                            }
                        }}
                        placeholder="Search tasks…"
                        aria-label="Search tasks"
                        className="h-9 w-full rounded-xl border border-white/10 bg-white/[0.03] pl-9 pr-8 text-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary/40 focus:bg-white/[0.05]"
                    />
                    {query && (
                        <button
                            onClick={() => onQueryChange('')}
                            aria-label="Clear search"
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </div>

                {/* Due filter, promoted out of the popover onto the bar itself —
                    "overdue" is the one filter worth a click rather than three.
                    Hidden below lg, where it would crowd the search; it stays
                    reachable in the popover at every width. */}
                <div
                    className="ml-auto hidden flex-shrink-0 items-center gap-1 lg:flex"
                    role="group"
                    aria-label="Filter by due date"
                >
                    {dueToggles}
                </div>

                {/* Filters */}
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            className={cn(
                                'flex h-9 flex-shrink-0 items-center gap-2 rounded-xl border px-3 text-sm transition-colors sm:ml-auto lg:ml-0',
                                activeChips.length
                                    ? 'border-primary/40 bg-primary/15 text-primary'
                                    : 'border-white/10 bg-white/[0.03] text-muted-foreground hover:border-white/20 hover:text-foreground'
                            )}
                        >
                            <SlidersHorizontal className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Filters</span>
                            {activeChips.length > 0 && (
                                <span className="rounded-full bg-primary/25 px-1.5 text-[10px] font-bold tabular-nums">
                                    {activeChips.length}
                                </span>
                            )}
                        </button>
                    </PopoverTrigger>
                    {/* Radix portals to <body>, so the surface's overflow-hidden
                        can't clip this. */}
                    <PopoverContent
                        align="end"
                        sideOffset={8}
                        className="w-64 rounded-2xl border-white/10 bg-[hsl(240_11%_7%)]/95 p-3 backdrop-blur-xl"
                    >
                        <div className="mb-3 lg:hidden">
                            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                Due
                            </p>
                            <div className="flex flex-wrap gap-1.5">{dueToggles}</div>
                        </div>
                        {FILTER_FIELDS.map((field) => (
                            <div key={field.key} className="mb-3 last:mb-0">
                                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                    {field.label}
                                </p>
                                <div className="flex gap-1.5">
                                    {LEVELS_DESC.map((level) => {
                                        const on = (filters[field.key] ?? []).includes(level);
                                        return (
                                            <button
                                                key={level}
                                                onClick={() => toggle(field.key, level)}
                                                aria-pressed={on}
                                                className={cn(
                                                    'flex-1 rounded-lg border px-2 py-1.5 text-[10px] font-bold uppercase transition-all',
                                                    on
                                                        ? field.meta[level].chip
                                                        : 'border-white/10 text-muted-foreground hover:border-white/20 hover:text-foreground'
                                                )}
                                            >
                                                {field.meta[level].label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </PopoverContent>
                </Popover>
            </div>

            {/* Active filters — the visible half of the popover trade, and the
                only place bulk actions appear. */}
            {isNarrowed && (
                <div className="flex flex-wrap items-center gap-1.5 border-t border-white/[0.06] px-2 py-2 md:px-3">
                    {activeChips.map((chip) => (
                        <button
                            key={`${chip.group}-${chip.value}`}
                            onClick={() => toggle(chip.group, chip.value)}
                            title="Remove filter"
                            className={cn(
                                'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase transition-opacity hover:opacity-75',
                                chip.chip
                            )}
                        >
                            {chip.label}
                            <X className="h-2.5 w-2.5" />
                        </button>
                    ))}
                    {activeChips.length > 0 && (
                        <button
                            onClick={() => onFiltersChange(EMPTY_FILTERS)}
                            className="ml-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
                        >
                            Clear
                        </button>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <span className="text-[11px] tabular-nums text-muted-foreground">
                            {shown} of {total}
                        </span>
                        {!readOnly && (
                            <BulkActions
                                count={shown}
                                mode={activeView === 'backlog' ? 'backlog' : 'active'}
                                onSetStatus={onBulkStatus}
                                onDelete={onBulkDelete}
                                onDemote={onBulkDemote}
                                onPromote={onBulkPromote}
                            />
                        )}
                    </div>
                </div>
            )}
        </Surface>
    );
}
